import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useUsers } from "../../../hooks/useUsers";
import UserTable from "../../../components/UserTable";
import AddUserForm from "../../../components/AddUserForm";
import { usePageContext } from "../../../context/PageContext";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";

const Users = () => {
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useUsers(currentPage, pageSize, search);

  useEffect(() => {
    setErrorMessage(error ? "Erro ao carregar usuários. Por favor, tente novamente." : null);
  }, [error]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setErrorMessage(null);
  };

  const handleDeleteUser = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir os usuários."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
    }
    refetch();
    setIsProcessing(false);
  };

  const confirmDeleteUser = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err.response?.data?.message || "Erro ao excluir o usuário.");
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const handleToggleStatus = (row) => {
    setIsProcessing(true);
    const next = Number(row.statusRaw) === 1 ? 0 : 1;
    updateMutation.mutate(
      { id: row.id, status: next, id_usuario_ultima_alteracao: userData?.id },
      {
        onSuccess: () => refetch(),
        onError: (err) => setErrorMessage(err.response?.data?.message || "Erro ao alterar status."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newSize) => setPageSize(newSize);
  const handleSearch = (term) => setSearch(term);
  const handleCloseForm = () => {
    setSelectedUser(null);
    setErrorMessage(null);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("usersPage.title")}</Typography>
        <Button variant="contained" fullWidth={isMobile} onClick={() => setSelectedUser({})}>
          {t("usersPage.addUser")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedUser ? (
        <AddUserForm
          user={selectedUser}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSaved={() => {
            refetch();
            handleCloseForm();
          }}
        />
      ) : (
        <UserTable
          users={data?.users || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? "Processando exclusão..." : "Tem certeza de que deseja excluir este usuário?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteUser}>Excluir</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
